import { FC, useCallback } from 'react';
import { Dropdown } from '@carbon/react';
import moment from 'moment';
import {
  FilterSection,
  ColumnDateFilter,
  ColumnMultiSelectFilter,
  useDataTableContext,
} from 'components/data-table';
import { DatePicker } from 'components/date-picker/date-picker';
import {
  STRIP_MODE_AS_OF,
  STRIP_MODE_MOST_RECENT,
  type PriceDeckFacets,
} from './types';
import { METHOD_TYPE_LABELS, STATUS_LABELS } from './utils';
import styles from './price-decks-filters.module.scss';

interface PriceDecksFiltersProps {
  facets: PriceDeckFacets | undefined;
}

const toOptions = (items: { id: string; value: string }[] | undefined) =>
  (items ?? []).map((item) => ({ value: item.id, label: item.value }));

const methodTypeOptions = Object.entries(METHOD_TYPE_LABELS).map(
  ([value, label]) => ({ value, label })
);

const statusOptions = Object.entries(STATUS_LABELS).map(([value, label]) => ({
  value,
  label,
}));

const STRIP_DATE_FILTER_ITEMS = [
  { id: '', label: 'All' },
  { id: STRIP_MODE_MOST_RECENT, label: 'Most Recent' },
  { id: STRIP_MODE_AS_OF, label: 'As-Of Date' },
];

const PriceDecksFilters: FC<PriceDecksFiltersProps> = ({ facets }) => {
  const { columnFilters, setColumnFilters } = useDataTableContext();

  const stripFilter = columnFilters.find((f) => f.id === 'stripPriceDate');
  const filterValue = stripFilter?.value as
    { mode: string; date?: string } | undefined;
  const selectedMode = filterValue?.mode ?? '';
  const selectedDate = filterValue?.date ?? null;

  const updateStripFilter = useCallback(
    (mode: string, date?: string) => {
      const newFilters = columnFilters.filter((f) => f.id !== 'stripPriceDate');
      if (mode) {
        newFilters.push({ id: 'stripPriceDate', value: { mode, date } });
      }
      setColumnFilters(newFilters);
    },
    [columnFilters, setColumnFilters]
  );

  const handleModeChange = useCallback(
    ({
      selectedItem,
    }: {
      selectedItem: { id: string; label: string } | null;
    }) => {
      const mode = selectedItem?.id ?? '';
      updateStripFilter(
        mode,
        mode === STRIP_MODE_AS_OF ? (selectedDate ?? undefined) : undefined
      );
    },
    [selectedDate, updateStripFilter]
  );

  const handleDateChange = useCallback(
    (dates: Date[]) => {
      if (dates.length > 0 && dates[0]) {
        updateStripFilter(
          STRIP_MODE_AS_OF,
          moment(dates[0]).format('YYYY-MM-DD')
        );
      }
    },
    [updateStripFilter]
  );

  const selectedModeItem =
    STRIP_DATE_FILTER_ITEMS.find((m) => m.id === selectedMode) ??
    STRIP_DATE_FILTER_ITEMS[0];

  return (
    <div className={styles.filtersWrapper}>
      <FilterSection title="Date Filters">
        <ColumnDateFilter columnId="createdAt" label="Created At" />
      </FilterSection>

      <FilterSection title="Price Deck Details">
        <ColumnMultiSelectFilter
          columnId="methodType"
          label="Pricing Method"
          options={methodTypeOptions}
        />

        <ColumnMultiSelectFilter
          columnId="createdBy"
          label="Created By"
          options={toOptions(facets?.createdByOptions)}
        />

        <ColumnMultiSelectFilter
          columnId="status"
          label="Status"
          options={statusOptions}
        />

        <div>
          <Dropdown
            id="strip-date-filter"
            titleText="Strip Price Date"
            label=""
            items={STRIP_DATE_FILTER_ITEMS}
            selectedItem={selectedModeItem}
            itemToString={(item: { id: string; label: string }) =>
              item?.label ?? ''
            }
            onChange={handleModeChange}
            light
          />
          {selectedMode === STRIP_MODE_AS_OF && (
            <DatePicker
              id="strip-date-filter-date"
              labelText="Date"
              value={selectedDate ?? undefined}
              onChange={handleDateChange}
              light
            />
          )}
        </div>
      </FilterSection>
    </div>
  );
};

export { PriceDecksFilters };
